<template>
    <div v-if="modelValue" class="overlay">
        <div class="dialog">
            <div class="icon">
                <SrIcon v-if="isSucceeded" icon="TickBrandBlue" data-test-id="VastGenerationStatus__success-icon" />
                <SrIcon v-if="isFailed" icon="CloseBrandBlue" data-test-id="VastGenerationStatus__failure-icon" />
                <div v-if="isLoading" class="loader">
                    <CircularLoader />
                </div>
            </div>
            <div class="text">
                <SrHeadline level="3" weight="bold" class="mb-0"> {{ userFriendlyStatus }} </SrHeadline>
                <SrHeadline v-if="isLoading" level="3" class="time-text" color="gray-darkest--text">
                    This may take up to a minute.
                </SrHeadline>
                <SrButton v-if="!isLoading" type="secondary" class="continue-button" @click="close"> Continue </SrButton>
            </div>
        </div>
    </div>
</template>
<script>
import { computed } from 'vue';
import { VAST_GENERATION_STATUS } from '@/services/video/VAST_GENERATION_STATUS';
import { SrHeadline, SrButton, SrIcon } from '@ads/design-system/src';
import CircularLoader from '@/components/domain/creatives/shared/CircularLoader';

export default {
    name: 'VastGenerationStatus',
    components: { CircularLoader, SrHeadline, SrButton, SrIcon },
    model: {
        prop: 'modelValue',
        event: 'update:modelValue',
    },
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        status: {
            type: String,
            required: false,
        },
    },
    setup(props, { emit }) {
        const userFriendlyStatusByStatus = new Map([
            [VAST_GENERATION_STATUS.TRANSCODING_WAITING, 'Starting the video transcoding process ...'],
            [VAST_GENERATION_STATUS.TRANSCODING_COMPLETED, 'Generating a VAST file for the uploaded video ...'],
            [VAST_GENERATION_STATUS.FAILED, 'Transcoding or VAST file creation process has failed.'],
            [VAST_GENERATION_STATUS.ABORTED, 'Transcoding process was cancelled.'],
            [VAST_GENERATION_STATUS.SUCCEEDED, 'VAST file has been successfully generated.'],
        ]);

        const userFriendlyStatus = computed(() => {
            if (props.status == null) {
                return 'Loading ...';
            }
            return userFriendlyStatusByStatus.get(props.status);
        });

        const isLoading = computed(() =>
            [undefined, null, VAST_GENERATION_STATUS.TRANSCODING_WAITING, VAST_GENERATION_STATUS.TRANSCODING_COMPLETED].includes(
                props.status,
            ),
        );

        const isFailed = computed(() => [VAST_GENERATION_STATUS.ABORTED, VAST_GENERATION_STATUS.FAILED].includes(props.status));

        const isSucceeded = computed(() => props.status === VAST_GENERATION_STATUS.SUCCEEDED);

        const close = () => {
            emit('update:modelValue', false);
        };

        return {
            userFriendlyStatus,
            isFailed,
            isSucceeded,
            isLoading,
            close,
        };
    },
};
</script>
<style scoped>
.overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: center;
}

.dialog {
    background-color: white;
    padding: 32px 24px 24px 24px;
    border-radius: 4px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 1010;
    display: flex;
    flex-direction: column;
    gap: 24px;
    justify-content: center;
    align-items: center;
    width: 400px;
}

.icon {
    margin: 12px 0 6px 0;
}

.loader {
    padding-bottom: 6px;
}

.text {
    text-align: center;
}

.time-text {
    margin: 12px 0 28px 0;
}

.continue-button {
    margin-top: 24px;
}
</style>
